import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home, Inbox, FileText, Users, Building2, Truck, Container, Network,
  Package, Settings, UserCog, FolderArchive, ChevronLeft, ChevronRight,
  PackageMinus, PackagePlus, Receipt, Truck as TruckOut,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useRichiesteCountStore } from '../../store/richiesteStore';
import { useOrdiniCountStore } from '../../store/ordiniStore';
import Logo from '../ui/Logo';
import { RICHIESTE_VIEW_ROLES, FATTURAZIONE_ROLES, SETTINGS_READ_ROLES, USCITE_ROLES } from '../../lib/constants';

interface MenuItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  badge: number | null;
  adminOnly?: boolean;
  // Visibile solo a questi ruoli (se assente: a tutti, salvo adminOnly).
  roles?: string[];
}

interface MenuSection {
  // title null = gruppo in cima senza intestazione (Dashboard)
  title: string | null;
  items: MenuItem[];
}

// Raggruppamento per leggibilità — le destinazioni (route) e la visibilità per
// ruolo restano identiche: l'unico filtro è `adminOnly` (Utenti/Settings),
// invariato rispetto al vecchio ADMIN_ITEMS.
// `label` e `title` sono CHIAVI i18n, tradotte al render con t().
const MENU_SECTIONS: MenuSection[] = [
  {
    title: null,
    items: [
      { path: '/dashboard', label: 'common.nav.dashboard', icon: Home, badge: null },
    ],
  },
  {
    title: 'common.nav.sezione_operativo',
    items: [
      { path: '/richieste', label: 'common.nav.richieste', icon: Inbox, badge: null, roles: RICHIESTE_VIEW_ROLES },
      { path: '/ordini', label: 'common.nav.ordini', icon: FileText, badge: null },
      { path: '/programmazione', label: 'common.nav.programmazione', icon: Truck, badge: null },
      { path: '/scarichi', label: 'common.nav.scarichi', icon: PackageMinus, badge: null },
      { path: '/produzioni', label: 'common.nav.produzioni', icon: PackagePlus, badge: null },
      { path: '/uscite', label: 'common.nav.uscite', icon: TruckOut, badge: null, roles: USCITE_ROLES },
      { path: '/fatturazione', label: 'common.nav.fatturazione', icon: Receipt, badge: null, roles: FATTURAZIONE_ROLES },
    ],
  },
  {
    title: 'common.nav.sezione_anagrafiche',
    items: [
      { path: '/clienti', label: 'common.nav.clienti', icon: Users, badge: null },
      { path: '/produttori', label: 'common.nav.produttori', icon: Building2, badge: null },
      { path: '/trasportatori', label: 'common.nav.trasportatori', icon: Container, badge: null },
      { path: '/intermediari', label: 'common.nav.intermediari', icon: Network, badge: null },
    ],
  },
  {
    title: 'common.nav.sezione_sistema',
    items: [
      { path: '/aree', label: 'common.nav.aree_stoccaggio', icon: Package, badge: null },
      { path: '/documenti-impianto', label: 'common.nav.documenti_impianto', icon: FolderArchive, badge: null },
      { path: '/utenti', label: 'common.nav.utenti', icon: UserCog, badge: null, adminOnly: true },
      { path: '/settings', label: 'common.nav.settings', icon: Settings, badge: null, roles: SETTINGS_READ_ROLES },
    ],
  },
];

const COLLAPSED_KEY = 'ellezeta_sidebar_collapsed';

function useCollapsed() {
  const stored = typeof window !== 'undefined'
    ? localStorage.getItem(COLLAPSED_KEY) === 'true'
    : false;
  const [collapsed, setCollapsed] = React.useState(stored);
  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_KEY, String(next));
      return next;
    });
  };
  return { collapsed, toggle };
}

import React from 'react';

export default function Sidebar() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { collapsed, toggle } = useCollapsed();
  const isAdmin = user?.role === 'admin';
  const richiesteCount = useRichiesteCountStore((s) => s.count);
  const ordiniCount = useOrdiniCountStore((s) => s.count);

  return (
    <aside
      className={`${collapsed ? 'w-[72px]' : 'w-60'} bg-white border-r border-stone-mid flex flex-col sticky top-0 h-screen flex-shrink-0 overflow-hidden`}
      style={{ transition: 'width 200ms ease' }}
    >
      {/* Logo */}
      <div
        className={`h-16 border-b border-stone-mid flex items-center flex-shrink-0 ${collapsed ? 'justify-center' : 'px-4'}`}
      >
        {collapsed ? (
          <div className="w-9 h-9 rounded-lg bg-ink-dark flex items-center justify-center text-white text-[13px] font-bold tracking-wide select-none">
            EZ
          </div>
        ) : (
          <Logo width={120} />
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 overflow-y-auto">
        {MENU_SECTIONS.map((section) => {
          // Filtro RBAC: `roles` (lista esplicita) ha priorità; altrimenti `adminOnly`;
          // altrimenti visibile a tutti.
          const visibleItems = section.items.filter((item) =>
            item.roles ? item.roles.includes(user?.role ?? '')
              : item.adminOnly ? isAdmin
              : true,
          );
          // Niente intestazione orfana: se la sezione non ha voci visibili, salta tutto.
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.title ?? '__top'}>
              {section.title && (
                <>
                  <div className="h-px bg-stone-mid mx-3 my-4" />
                  {!collapsed && (
                    <div className="text-[11px] font-semibold text-ink-faint uppercase tracking-[0.06em] px-3 mb-2">
                      {t(section.title)}
                    </div>
                  )}
                </>
              )}
              {visibleItems.map((item) => (
                <NavItem
                  key={item.path}
                  item={
                    item.path === '/richieste'
                      ? { ...item, badge: richiesteCount > 0 ? richiesteCount : null }
                      : item.path === '/ordini'
                      ? { ...item, badge: ordiniCount > 0 ? ordiniCount : null }
                      : item
                  }
                  collapsed={collapsed}
                />
              ))}
            </div>
          );
        })}
      </nav>

      {/* Toggle */}
      <div className="p-3 border-t border-stone-mid flex-shrink-0">
        <button
          type="button"
          onClick={toggle}
          title={collapsed ? t('common.nav.espandi_sidebar') : t('common.nav.comprimi_sidebar')}
          className={`w-full flex items-center ${collapsed ? 'justify-center' : 'justify-end gap-1'} px-2 py-2 rounded-md text-ink-soft hover:bg-stone-bg hover:text-ink-dark transition-colors text-xs`}
        >
          {collapsed ? <ChevronRight size={16} /> : (
            <>
              <ChevronLeft size={16} />
              <span>{t('common.nav.comprimi')}</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}

function NavItem({ item, collapsed }: { item: MenuItem; collapsed: boolean }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      title={collapsed ? t(item.label) : undefined}
      end={item.path === '/richieste' ? false : undefined}
      className={({ isActive }) =>
        `relative w-full flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 mb-0.5 rounded-md text-sm transition-colors duration-100 ` +
        (isActive
          ? 'bg-sage-light text-sage-dark font-semibold'
          : 'text-ink-mid font-medium hover:bg-stone-bg hover:text-ink-dark')
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-2 bottom-2 w-0.5 bg-sage rounded-r-sm" />
          )}
          <Icon
            size={18}
            className={`flex-shrink-0 ${isActive ? 'text-sage' : 'text-ink-soft'}`}
          />
          {!collapsed && (
            <>
              <span className="flex-1 truncate">{t(item.label)}</span>
              {item.badge != null && (
                <span className="min-w-[22px] h-5 px-1.5 text-[11px] font-semibold text-white bg-sage rounded-full flex items-center justify-center">
                  {item.badge}
                </span>
              )}
            </>
          )}
          {collapsed && item.badge != null && (
            <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 text-[10px] font-bold text-white bg-sage rounded-full flex items-center justify-center">
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
